import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PricingCard } from './PricingCard';
import { BillingToggle } from './BillingToggle';
import { PlanChangeConfirmModal } from './PlanChangeConfirmModal';
import { PRICING_TIERS, getTierById } from './constants';
import { BillingCycle, PricingTier } from './types';
import { useAuth } from '@/context/AuthContext';
import { useSubscription, SubscriptionProvider } from '@/context/SubscriptionContext';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';

// Inner component that uses subscription context
const PricingCardsGridInner: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    subscription,
    currentPlan,
    updateSubscription,
    createSubscription,
    isLoading: subscriptionLoading
  } = useSubscription();

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const hasActiveSubscription = subscription &&
    (subscription.status === 'active' || subscription.status === 'trialing');

  // Map database plan name to tier ID
  const currentTierId = currentPlan?.name?.toLowerCase() || null;

  // Find current tier from PRICING_TIERS
  const currentTier = currentTierId ? getTierById(currentTierId) : null;

  const handlePlanSelect = (tierId: string) => {
    // If not logged in, redirect to sign up with plan
    if (!user) {
      const tier = getTierById(tierId);
      if (tier) {
        navigate(tier.ctaLink);
      }
      return;
    }

    const tier = getTierById(tierId);
    if (!tier) return;

    // Enterprise plan - redirect to contact
    if (tier.monthlyPrice === null) {
      navigate('/contact?plan=enterprise');
      return;
    }

    // If same plan and same billing cycle, do nothing
    if (currentTierId === tierId && subscription?.billing_cycle === billingCycle) {
      toast.info('You\'re already on this plan');
      return;
    }

    // Show confirmation modal
    setSelectedTier(tier);
    setShowConfirmModal(true);
  };

  const handleConfirmPlanChange = async () => {
    if (!selectedTier) return;

    setIsProcessing(true);
    try {
      if (hasActiveSubscription) {
        // Update existing subscription
        await updateSubscription(selectedTier.id, billingCycle);
        toast.success('Plan updated successfully!', {
          description: `You're now on the ${selectedTier.name} plan.`
        });
      } else {
        // Create new subscription
        const checkoutUrl = await createSubscription(selectedTier.id, billingCycle);
        if (checkoutUrl && checkoutUrl.startsWith('http')) {
          // Redirect to Stripe checkout
          window.location.href = checkoutUrl;
          return;
        }
        toast.success('Plan activated!', {
          description: `You're now on the ${selectedTier.name} plan.`
        });
      }
    } catch (error: any) {
      console.error('Plan change error:', error);

      // Check if we need to create a new subscription (e.g., upgrading from free)
      if (error?.message?.includes('requires_checkout') || error?.requires_checkout) {
        try {
          const checkoutUrl = await createSubscription(selectedTier.id, billingCycle);
          if (checkoutUrl && checkoutUrl.startsWith('http')) {
            window.location.href = checkoutUrl;
            return;
          }
        } catch (checkoutError) {
          toast.error('Failed to start checkout', {
            description: 'Please try again or contact support.'
          });
        }
      } else {
        toast.error('Failed to change plan', {
          description: error?.message || 'Please try again or contact support.'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section className="py-8 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Current Plan Indicator */}
        {hasActiveSubscription && currentTier && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Alert className="bg-primary/5 border-primary/20">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription className="flex items-center gap-2">
                <span>You're currently on the</span>
                <Badge className="font-semibold">
                  {currentTier.name}
                </Badge>
                <span>plan ({subscription.billing_cycle})</span>
                {subscription.cancel_at_period_end && (
                  <Badge variant="destructive" className="ml-2">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Cancels soon
                  </Badge>
                )}
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex items-center bg-muted/50 backdrop-blur-sm rounded-full px-6 py-3 border border-border/50">
            <BillingToggle
              billingCycle={billingCycle}
              onToggle={setBillingCycle}
            />
          </div>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-4 xl:gap-6 items-stretch">
          {PRICING_TIERS.map((tier, index) => {
            const isCurrentPlan = currentTierId === tier.id;

            return (
              <div key={tier.id} className="relative">
                {/* Current Plan Badge */}
                {isCurrentPlan && hasActiveSubscription && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 z-30"
                  >
                    <Badge className="bg-green-500 text-white px-3 py-1 shadow-md">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Your Plan
                    </Badge>
                  </motion.div>
                )}

                <PricingCard
                  tier={tier}
                  billingCycle={billingCycle}
                  index={index}
                  onSelect={user ? handlePlanSelect : undefined}
                  isSelected={isCurrentPlan && hasActiveSubscription}
                  isLoading={isProcessing && selectedTier?.id === tier.id}
                />
              </div>
            );
          })}
        </div>

        {/* Trust note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-center text-sm text-muted-foreground mt-12"
        >
          All plans include secure data encryption, automatic backups, and GDPR compliance.
          <br className="hidden sm:block" />
          {' '}You can upgrade, downgrade, or cancel anytime.
        </motion.p>

        {/* Plan Change Confirmation Modal */}
        {selectedTier && (
          <PlanChangeConfirmModal
            open={showConfirmModal}
            onOpenChange={setShowConfirmModal}
            currentPlan={currentTier}
            newPlan={selectedTier}
            billingCycle={billingCycle}
            onConfirm={handleConfirmPlanChange}
          />
        )}
      </div>
    </section>
  );
};

// Wrapper component that provides subscription context when user is logged in
export const PricingCardsGrid: React.FC = () => {
  const { user } = useAuth();

  // If user is logged in, wrap with SubscriptionProvider
  if (user) {
    return (
      <SubscriptionProvider>
        <PricingCardsGridInner />
      </SubscriptionProvider>
    );
  }

  // If not logged in, render without subscription features
  return <PricingCardsGridInner />;
};

export default PricingCardsGrid;
